import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OfficeDetails = () => {
  const officeInfo = {
    address: "Mava Cold Beverages Pvt. Ltd.\nAnantapur, Andhra Pradesh - 515001\nINDIA",
    phone: "+91 8919405132",
    email: "support@mavacoldbeverages.in",
    whatsapp: "+91 8919405132",
    businessHours: [
      { day: "Monday - Friday", hours: "9:00 AM - 6:00 PM" },
      { day: "Saturday", hours: "10:00 AM - 4:00 PM" },
      { day: "Sunday", hours: "Closed" }
    ]
  };

  const socialLinks = [
    { name: "Instagram", icon: "Instagram", url: "https://instagram.com/mava.beverages", color: "text-pink-500" },
    { name: "Facebook", icon: "Facebook", url: "https://facebook.com/mavabeverages", color: "text-blue-600" },
    { name: "Twitter", icon: "Twitter", url: "https://twitter.com/mavabeverages", color: "text-blue-400" },
    { name: "LinkedIn", icon: "Linkedin", url: "https://linkedin.com/company/mava-beverages", color: "text-blue-700" }
  ];

  const handleCall = () => {
    window.open(`tel:${officeInfo?.phone}`, '_self');
  };

  const handleEmail = () => {
    window.open(`mailto:${officeInfo?.email}`, '_self');
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${officeInfo?.whatsapp?.replace(/[^0-9]/g, '')}`, '_blank');
  };

  return (
    <div className="space-y-8">
      {/* Office Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-card rounded-2xl liquid-shadow p-8"
      >
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
            <Icon name="MapPin" size={24} color="white" />
          </div>
          <div>
            <h3 className="text-xl font-poppins font-bold text-text-primary">
              Visit Our Office
            </h3>
            <p className="text-text-secondary">
              Come experience Mava in person
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            {/* Address */}
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                <Icon name="Building" size={20} className="text-text-secondary" />
              </div>
              <div>
                <h4 className="font-semibold text-text-primary mb-2">Address</h4>
                <p className="text-text-secondary whitespace-pre-line leading-relaxed">
                  {officeInfo?.address}
                </p>
              </div>
            </div>

            {/* Contact Methods */}
            <div className="space-y-4">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="flex items-center space-x-4 p-4 bg-surface rounded-lg cursor-pointer brand-transition"
                onClick={handleCall}
              >
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Phone" size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h5 className="font-medium text-text-primary">Phone</h5>
                  <p className="text-text-secondary">{officeInfo?.phone}</p>
                </div>
                <Icon name="ExternalLink" size={16} className="text-text-secondary" />
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="flex items-center space-x-4 p-4 bg-surface rounded-lg cursor-pointer brand-transition"
                onClick={handleEmail}
              >
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Mail" size={20} className="text-accent" />
                </div>
                <div className="flex-1">
                  <h5 className="font-medium text-text-primary">Email</h5>
                  <p className="text-text-secondary">{officeInfo?.email}</p>
                </div>
                <Icon name="ExternalLink" size={16} className="text-text-secondary" />
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                className="flex items-center space-x-4 p-4 bg-surface rounded-lg cursor-pointer brand-transition"
                onClick={handleWhatsApp}
              >
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Icon name="MessageCircle" size={20} className="text-green-600" />
                </div>
                <div className="flex-1">
                  <h5 className="font-medium text-text-primary">WhatsApp</h5>
                  <p className="text-text-secondary">{officeInfo?.whatsapp}</p>
                </div>
                <Icon name="ExternalLink" size={16} className="text-text-secondary" />
              </motion.div>
            </div>
          </div>

          {/* Business Hours */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                <Icon name="Clock" size={20} className="text-text-secondary" />
              </div>
              <h4 className="font-semibold text-text-primary">Business Hours</h4>
            </div>
            
            <div className="space-y-3">
              {officeInfo?.businessHours?.map((schedule, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-surface rounded-lg">
                  <span className="font-medium text-text-primary">{schedule?.day}</span>
                  <span className={`text-sm ${
                    schedule?.hours === 'Closed' ?'text-red-500' :'text-text-secondary'
                  }`}>
                    {schedule?.hours}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Info" size={16} className="text-primary" />
                <span className="text-sm font-medium text-primary">Quick Tip</span>
              </div>
              <p className="text-sm text-text-secondary">
                Schedule a visit in advance to ensure our product specialists are available for personalized consultation.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
      {/* Interactive Map */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-card rounded-2xl liquid-shadow overflow-hidden"
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-accent to-primary rounded-lg flex items-center justify-center">
                <Icon name="Map" size={20} color="white" />
              </div>
              <div>
                <h3 className="text-lg font-poppins font-bold text-text-primary">
                  Find Us on Map
                </h3>
                <p className="text-text-secondary text-sm">
                  Anantapur, Andhra Pradesh - 515001
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="Navigation"
              iconPosition="left"
              onClick={() => window.open('https://maps.google.com/?q=14.698039,77.574948', '_blank')}
            >
              Get Directions
            </Button>
          </div>
        </div>
        
        <div className="h-80 w-full">
          <iframe
            width="100%"
            height="100%"
            loading="lazy"
            title="Mava Cold Beverages Office Location"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps?q=14.698039,77.574948&z=15&output=embed"
            className="border-0"
          />
        </div>
      </motion.div>
      {/* Social Media Links */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-card rounded-2xl liquid-shadow p-8"
      >
        <div className="text-center mb-6">
          <h3 className="text-xl font-poppins font-bold text-text-primary mb-2">
            Connect With Us
          </h3>
          <p className="text-text-secondary">
            Follow our journey and stay updated with the latest from Mava
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {socialLinks?.map((social, index) => (
            <motion.a
              key={social?.name}
              href={social?.url}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="flex flex-col items-center p-6 bg-surface rounded-xl hover:bg-muted brand-transition group"
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 brand-transition ${
                social?.name === 'Instagram' ? 'bg-pink-50' :
                social?.name === 'Facebook' ? 'bg-blue-50' :
                social?.name === 'Twitter'? 'bg-blue-50' : 'bg-blue-50'
              }`}>
                <Icon name={social?.icon} size={24} className={social?.color} />
              </div>
              <span className="text-sm font-medium text-text-primary group-hover:text-primary brand-transition">
                {social?.name}
              </span>
            </motion.a>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default OfficeDetails;